import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { collection, query, where, getDocs, writeBatch, doc } from 'firebase/firestore';
import { GRADOS } from '../../constants';

export default function Promotions({ user, currentYear }) {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [targetYear, setTargetYear] = useState(currentYear + 1);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!user || !currentYear) return;
        fetchStudents();
    }, [user, currentYear]);

    const fetchStudents = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, "estudiantes"), where("profesorId", "==", user.uid));
            const querySnapshot = await getDocs(q);
            const list = querySnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(s => (s.year || 2025) === currentYear);

            // Sort by grade (Pre-Jardin -> Jardin -> Transicion) for better visualization
            list.sort((a, b) => {
                const gradeOrder = { "Pre-Jardín": 1, "Jardín": 2, "Transición": 3 };
                return (gradeOrder[a.grado] || 99) - (gradeOrder[b.grado] || 99);
            });

            setStudents(list);
        } catch (err) {
            console.error("Error fetching students:", err);
            setError("Error al cargar estudiantes.");
        } finally {
            setLoading(false);
        }
    };

    const getNextGrade = (currentGrade) => {
        if (currentGrade === "Pre-Jardín") return "Jardín";
        if (currentGrade === "Jardín") return "Transición";
        return "Graduado"; // Transición students don't continue in this system usually
    };

    const handlePromotion = async () => {
        if (!window.confirm(`¿Estás seguro de promover a estos estudiantes al año ${targetYear}?`)) return;

        setProcessing(true);
        setMessage('');
        setError('');

        try {
            const batch = writeBatch(db);
            let promotedCount = 0;

            for (const student of students) {
                const nextGrade = getNextGrade(student.grado);

                // Skip students who are graduating (unless you want to keep them as alumni record, but prompt said "Transition students are not promoted")
                // User said: "Transition students are not promoted, but their information is saved" (implied in old year).
                // So we only copy if nextGrade != Graduado

                if (nextGrade !== "Graduado") {
                    const newStudentRef = doc(collection(db, "estudiantes")); // Auto-ID for new year record
                    batch.set(newStudentRef, {
                        ...student,
                        grado: nextGrade,
                        year: targetYear,
                        promotedFrom: student.id // Optional: link to previous year record
                    });
                    // Remove id from spread if it exists in data, we want new ID
                    // Actually student object has 'id' from the doc.id which we spread. 
                    // Batch.set overwrites. We should construct object explicitly to avoid overwriting ID field if it's in data.
                    // But student object from hook *has* id property. 
                    // Let's be safe:
                }
            }

            // Correction for loop above to be cleaner
            const newStudents = students.filter(s => getNextGrade(s.grado) !== "Graduado").map(s => ({
                nombreCompleto: s.nombreCompleto,
                documentoIdentidad: s.documentoIdentidad,
                profesorId: s.profesorId,
                grado: getNextGrade(s.grado),
                year: targetYear,
                promotedFrom: s.id
            }));

            if (newStudents.length === 0) {
                setMessage("No hay estudiantes elegibles para promoción (todos son Transición o lista vacía).");
                setProcessing(false);
                return;
            }

            newStudents.forEach(s => {
                const ref = doc(collection(db, "estudiantes"));
                batch.set(ref, s);
            });

            await batch.commit();
            setMessage(`¡Promoción exitosa! ${newStudents.length} estudiantes han sido promovidos al año ${targetYear}.`);
            setPromotedCount(newStudents.length);

        } catch (err) {
            console.error("Promotion error:", err);
            setError("Ocurrió un error durante la promoción: " + err.message);
        } finally {
            setProcessing(false);
        }
    };

    const [promotedCount, setPromotedCount] = useState(0);

    return (
        <div className="glass-card p-8 rounded-2xl animate-fade-in">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-slate-800 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-secondary-600">
                        Promoción de Estudiantes
                    </h2>
                    <p className="text-slate-500 text-sm mt-1">
                        Mover estudiantes del año <span className="font-bold text-slate-800">{currentYear}</span> al <span className="font-bold text-slate-800">{targetYear}</span>
                    </p>
                </div>

                <div className="flex items-center gap-4 bg-slate-50 p-2 rounded-xl border border-slate-200">
                    <span className="text-sm font-bold text-slate-600 pl-2">Año Destino:</span>
                    <input
                        type="number"
                        value={targetYear}
                        onChange={(e) => setTargetYear(Number(e.target.value))}
                        className="glass-input w-24 py-1 px-3 text-center font-bold"
                    />
                </div>
            </div>

            {message && <div className="mb-6 p-4 rounded-xl bg-green-50 text-green-700 font-medium border border-green-100 animate-pulse">{message}</div>}
            {error && <div className="mb-6 p-4 rounded-xl bg-red-50 text-red-700 font-medium border border-red-100 animate-pulse">{error}</div>}

            <div className="mb-6 p-4 bg-blue-50 text-blue-800 rounded-xl border border-blue-100 text-sm flex items-start gap-3">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <div>
                    <span className="font-bold block mb-1">¿Cómo funciona la promoción?</span>
                    Esta acción creará una <strong>copia</strong> de los estudiantes listados abajo en el año <strong>{targetYear}</strong> con su nuevo grado.
                    <ul className="list-disc list-inside mt-1 ml-1 opacity-90">
                        <li>Pre-Jardín → Jardín</li>
                        <li>Jardín → Transición</li>
                        <li>Transición → <em>No se promueve (Finaliza)</em></li>
                    </ul>
                    Los datos del año {currentYear} permanecerán intactos.
                </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200/50 mb-6">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-200/50 text-slate-500 uppercase text-xs tracking-wider">
                            <th className="p-4 font-bold">Estudiante</th>
                            <th className="p-4 font-bold">Grado Actual ({currentYear})</th>
                            <th className="p-4 font-bold text-primary-600">→</th>
                            <th className="p-4 font-bold text-primary-700">Grado Destino ({targetYear})</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan="4" className="p-8 text-center">Cargando...</td></tr>
                        ) : students.length === 0 ? (
                            <tr><td colSpan="4" className="p-8 text-center text-slate-400">No hay estudiantes en este año.</td></tr>
                        ) : (
                            students.map(s => {
                                const nextGrade = getNextGrade(s.grado);
                                const isGraduating = nextGrade === "Graduado";
                                return (
                                    <tr key={s.id} className="hover:bg-slate-50/30">
                                        <td className="p-4 font-medium">{s.nombreCompleto}</td>
                                        <td className="p-4 text-slate-500">{s.grado}</td>
                                        <td className="p-4 text-primary-300">→</td>
                                        <td className="p-4">
                                            <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${isGraduating ? 'bg-slate-100 text-slate-500' : 'bg-green-100 text-green-700'}`}>
                                                {nextGrade}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex justify-end">
                <button
                    onClick={handlePromotion}
                    disabled={processing || loading || students.length === 0}
                    className="btn-primary px-8 py-3 rounded-xl font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-primary-500/30"
                >
                    {processing ? 'Procesando...' : 'Confirmar e Iniciar Promoción'}
                </button>
            </div>
        </div>
    );
}
